import { CustomFieldDefinition } from '../lib/api'

interface CustomFieldsFormProps {
  fields: CustomFieldDefinition[]
  values: Record<string, any>
  onChange: (fieldName: string, value: any) => void
  loading?: boolean
}

/**
 * Reusable component for rendering custom field inputs in entity forms.
 * Supports text, number, date, select, checkbox, textarea, email, and url field types.
 */
export default function CustomFieldsForm({
  fields,
  values,
  onChange,
  loading = false,
}: CustomFieldsFormProps) {
  if (loading) {
    return (
      <div className="py-4 text-center text-gray-500 text-sm">
        Loading custom fields...
      </div>
    )
  }

  if (fields.length === 0) {
    return null
  }

  const activeFields = fields.filter((f) => f.active)

  if (activeFields.length === 0) {
    return null
  }

  const renderField = (field: CustomFieldDefinition) => {
    const value = values[field.field_name] ?? field.default_value ?? ''
    const fieldId = `custom-field-${field.field_name}`

    switch (field.field_type) {
      case 'text':
        return (
          <input
            id={fieldId}
            type="text"
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            className="input"
            required={field.required}
          />
        )

      case 'number':
        return (
          <input
            id={fieldId}
            type="number"
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value ? Number(e.target.value) : '')}
            className="input"
            required={field.required}
          />
        )

      case 'date':
        return (
          <input
            id={fieldId}
            type="date"
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            className="input"
            required={field.required}
          />
        )

      case 'select':
        const options = field.options || []
        return (
          <select
            id={fieldId}
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            className="input"
            required={field.required}
          >
            <option value="">Select...</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )

      case 'checkbox':
        return (
          <div className="flex items-center">
            <input
              id={fieldId}
              type="checkbox"
              checked={value === true || value === 'true' || value === 1}
              onChange={(e) => onChange(field.field_name, e.target.checked)}
              className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
            />
            <label htmlFor={fieldId} className="ml-2 text-sm text-gray-700">
              {field.field_label}
            </label>
          </div>
        )

      case 'textarea':
        return (
          <textarea
            id={fieldId}
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            className="input"
            rows={3}
            required={field.required}
          />
        )

      case 'email':
        return (
          <input
            id={fieldId}
            type="email"
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            className="input"
            required={field.required}
          />
        )

      case 'url':
        return (
          <input
            id={fieldId}
            type="url"
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            className="input"
            placeholder="https://"
            required={field.required}
          />
        )

      default:
        return (
          <input
            id={fieldId}
            type="text"
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            className="input"
            required={field.required}
          />
        )
    }
  }

  return (
    <div className="border-t pt-4 mt-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Custom Fields</h3>
      <div className="space-y-4">
        {activeFields.map((field) => (
          <div key={field.id}>
            {field.field_type !== 'checkbox' && (
              <label htmlFor={`custom-field-${field.field_name}`} className="label">
                {field.field_label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            {renderField(field)}
          </div>
        ))}
      </div>
    </div>
  )
}
